import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Suspense } from 'react';
import * as Sentry from '@sentry/react';
import { QueryProvider, SuperTokensProvider, ThemeProvider, UsertourProvider } from '@/providers';
import { PageLoader, ErrorBoundary, lazyWithRetry } from '@petseo/shared';
import { AppLayout } from '@/components/layout/app/AppLayout';
import { ProtectedRoute } from '@/features/auth';

const LoginPage = lazyWithRetry(() => import('@/features/auth').then((module) => ({ default: module.LoginPage })));
const RegisterPage = lazyWithRetry(() => import('@/features/auth').then((module) => ({ default: module.RegisterPage })));
const ForgotPasswordPage = lazyWithRetry(() => import('@/features/auth').then((module) => ({ default: module.ForgotPasswordPage })));
const DashboardPage = lazyWithRetry(() => import('@/features/dashboard').then((module) => ({ default: module.DashboardPage })));
const ProfilePage = lazyWithRetry(() => import('@/features/service-vendors').then((module) => ({ default: module.ProfilePage })));
const ProfileAddPage = lazyWithRetry(() => import('@/features/service-vendors').then((module) => ({ default: module.ProfileAddPage })));
const ProfileDetailsPage = lazyWithRetry(() => import('@/features/service-vendors').then((module) => ({ default: module.ProfileDetailsPage })));
const CalendarPage = lazyWithRetry(() => import('@/features/availability').then((module) => ({ default: module.CalendarPage })));
const ReservationsPage = lazyWithRetry(() => import('@/features/reservations').then((module) => ({ default: module.ReservationsPage })));
const ReservationDetailsPage = lazyWithRetry(() => import('@/features/reservations').then((module) => ({ default: module.ReservationDetailsPage })));
const ExternalReservationDetailsPage = lazyWithRetry(() =>
  import('@/features/reservations').then((module) => ({ default: module.ExternalReservationDetailsPage }))
);
const SubscriptionPage = lazyWithRetry(() => import('@/features/subscription').then((module) => ({ default: module.SubscriptionPage })));

function App() {
  return (
    <ErrorBoundary onError={(error, errorInfo) => Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } })}>
      <SuperTokensProvider>
        <ThemeProvider>
          <QueryProvider>
            <UsertourProvider>
              <BrowserRouter>
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/register" element={<RegisterPage />} />
                    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                    <Route
                      path="/"
                      element={
                        <ProtectedRoute>
                          <AppLayout />
                        </ProtectedRoute>
                      }>
                      <Route index element={<Navigate to="/dashboard" replace />} />
                      <Route path="dashboard" element={<DashboardPage />} />
                      <Route path="profiles" element={<ProfilePage />} />
                      <Route path="profiles/add" element={<ProfileAddPage />} />
                      <Route path="profiles/:id" element={<ProfileDetailsPage />} />
                      <Route path="calendar" element={<CalendarPage />} />
                      <Route path="reservations" element={<ReservationsPage />} />
                      <Route path="reservations/:vendorId/:employeeId/external/:id" element={<ExternalReservationDetailsPage />} />
                      <Route path="reservations/:vendorId/:id" element={<ReservationDetailsPage />} />
                      <Route path="subscription" element={<SubscriptionPage />} />
                      <Route path="*" element={<Navigate to="/dashboard" replace />} />
                    </Route>
                  </Routes>
                </Suspense>
              </BrowserRouter>
            </UsertourProvider>
          </QueryProvider>
        </ThemeProvider>
      </SuperTokensProvider>
    </ErrorBoundary>
  );
}

export default App;
